import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import {
  addFavorite as addFavoriteApi,
  getRecordingFiles as getRecordingFilesApi,
  listFavoriteIDs as listFavoriteIDsApi,
  listFavorites as listFavoritesApi,
  ListRecordingsParams,
  ListRecordingsResult,
  listRecordings as listRecordingsApi,
  listRubrics as listRubricsApi,
  removeFavorite as removeFavoriteApi,
} from "../services/recording";
import {
  Recording,
  RecordingCategory,
  RecordingFilesCacheEntry,
} from "../model/recording";
import { Rubric } from "../model/rubric";

interface SearchFilters {
  category?: RecordingCategory;
  rubricID?: string;
}

type PersistedRubricSelectionState = Partial<
  Record<"teaching" | "hymn", string>
>;

interface ArchiveState {
  filterMode: "date" | "categoryOrRubric";
  selectedMonth: string;
  selectedYear: string;
  selectedRubricID: string;
  filteredRecordings: Recording[];
  hasAppliedFilter: boolean;
}

type RecordingInitializationState = Partial<
  Record<RecordingCategory | "hymn" | "favorites" | "archive", boolean>
>;

const getCurrentMonth = (): string =>
  `${new Date().getMonth() + 1}`.padStart(2, "0");

const getCurrentYear = (): string => String(new Date().getFullYear());

const buildDefaultArchiveState = (): ArchiveState => ({
  filterMode: "date",
  selectedMonth: getCurrentMonth(),
  selectedYear: getCurrentYear(),
  selectedRubricID: "",
  filteredRecordings: [],
  hasAppliedFilter: false,
});

export interface RecordingContextModel {
  rubrics: Rubric[];
  recordingsByCategory: Partial<Record<RecordingCategory, Recording[]>>;
  recordingsByRubric: Record<string, Recording[]>;
  favorites: Recording[];
  favoriteIDs: string[];
  hasLoadedRubrics: boolean;
  hasLoadedFavorites: boolean;
  hasLoadedFavoriteIDs: boolean;
  hasInitialized: RecordingInitializationState;
  setHasInitialized: Dispatch<SetStateAction<RecordingInitializationState>>;
  selectedRubricIDs: PersistedRubricSelectionState;
  setSelectedRubricIDs: Dispatch<SetStateAction<PersistedRubricSelectionState>>;
  archiveState: ArchiveState;
  setArchiveState: Dispatch<SetStateAction<ArchiveState>>;
  hasLoadedCategory: (category: RecordingCategory) => boolean;
  hasLoadedRubricResult: (
    category: RecordingCategory,
    rubricID: string,
  ) => boolean;
  recordingFilesMap: Map<string, RecordingFilesCacheEntry>;
  isLoading: boolean;
  error: string | null;
  listRubrics: () => Promise<Rubric[]>;
  listRecordingsByCategory: (
    category: RecordingCategory,
    options?: Omit<ListRecordingsParams, "category" | "rubricID">,
  ) => Promise<ListRecordingsResult>;
  listRecordingsByRubric: (
    category: RecordingCategory,
    rubricID: string,
    options?: Omit<ListRecordingsParams, "category" | "rubricID">,
  ) => Promise<Recording[]>;
  listFavorites: () => Promise<Recording[]>;
  listFavoriteIDs: () => Promise<string[]>;
  addFavorite: (recordingID: string, recording?: Recording) => Promise<void>;
  removeFavorite: (recordingID: string) => Promise<void>;
  isFavorite: (recordingID: string) => boolean;
  searchRecordings: (query: string, filters?: SearchFilters) => Recording[];
  getRecordingFiles: (
    recordingID: string,
    forceRefresh?: boolean,
  ) => Promise<RecordingFilesCacheEntry>;
}

const RecordingContext = createContext<RecordingContextModel | undefined>(
  undefined,
);

const buildRubricFilterKey = (
  category: RecordingCategory,
  rubricID: string,
): string => `${category}:${rubricID}`;

const uniqueRecordings = (recordings: Recording[]): Recording[] => {
  const byID = new Map<string, Recording>();
  recordings.forEach((recording) => {
    byID.set(recording.recordingID, recording);
  });
  return Array.from(byID.values());
};

const mergeRecordings = (
  current: Recording[] = [],
  incoming: Recording[] = [],
): Recording[] => {
  if (current.length === 0) {
    return incoming;
  }

  return uniqueRecordings([...current, ...incoming]);
};

const toEpochMs = (timestamp: number): number =>
  timestamp > 1_000_000_000_000 ? timestamp : timestamp * 1000;

export const RecordingProvider = ({ children }: { children: ReactNode }) => {
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [recordingsByCategory, setRecordingsByCategory] = useState<
    Partial<Record<RecordingCategory, Recording[]>>
  >({});
  const [recordingsByRubric, setRecordingsByRubric] = useState<
    Record<string, Recording[]>
  >({});
  const [favorites, setFavorites] = useState<Recording[]>([]);
  const [favoriteIDs, setFavoriteIDs] = useState<string[]>([]);
  const [hasLoadedRubrics, setHasLoadedRubrics] = useState(false);
  const [hasLoadedFavorites, setHasLoadedFavorites] = useState(false);
  const [hasLoadedFavoriteIDs, setHasLoadedFavoriteIDs] = useState(false);
  const [hasInitialized, setHasInitialized] =
    useState<RecordingInitializationState>({});
  const [selectedRubricIDs, setSelectedRubricIDs] =
    useState<PersistedRubricSelectionState>({});
  const [archiveState, setArchiveState] = useState<ArchiveState>(
    buildDefaultArchiveState,
  );
  const [loadedCategories, setLoadedCategories] = useState<
    Partial<Record<RecordingCategory, boolean>>
  >({});
  const [loadedRubricResults, setLoadedRubricResults] = useState<
    Record<string, boolean>
  >({});
  const [recordingFilesMap, setRecordingFilesMap] = useState<
    Map<string, RecordingFilesCacheEntry>
  >(() => new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const withLoading = useCallback(async <T,>(fn: () => Promise<T>) => {
    setIsLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const listRubrics = useCallback(
    async (): Promise<Rubric[]> =>
      withLoading(async () => {
        const result = await listRubricsApi();
        const sortedRubrics = result.rubrics.sort((a, b) => b.date - a.date);
        setRubrics(sortedRubrics);
        setHasLoadedRubrics(true);
        return sortedRubrics;
      }),
    [withLoading],
  );

  const listRecordingsByCategory = useCallback(
    async (
      category: RecordingCategory,
      options?: Omit<ListRecordingsParams, "category" | "rubricID">,
    ): Promise<ListRecordingsResult> =>
      withLoading(async () => {
        const result = await listRecordingsApi({
          category,
          from: options?.from,
          to: options?.to,
          limit: options?.limit,
          nextPageToken: options?.nextPageToken,
        });

        const shouldAppend = Boolean(options?.nextPageToken);
        setRecordingsByCategory((prev) => {
          const existing = prev[category] ?? [];
          return {
            ...prev,
            [category]: shouldAppend
              ? mergeRecordings(existing, result.recordings)
              : result.recordings,
          };
        });
        setLoadedCategories((prev) => ({
          ...prev,
          [category]: true,
        }));

        return result;
      }),
    [withLoading],
  );

  const listRecordingsByRubric = useCallback(
    async (
      category: RecordingCategory,
      rubricID: string,
      options?: Omit<ListRecordingsParams, "category" | "rubricID">,
    ): Promise<Recording[]> =>
      withLoading(async () => {
        const result = await listRecordingsApi({
          category,
          rubricID,
          from: options?.from,
          to: options?.to,
          nextPageToken: options?.nextPageToken,
        });

        const key = buildRubricFilterKey(category, rubricID);
        setRecordingsByRubric((prev) => ({
          ...prev,
          [key]: result.recordings,
        }));
        setLoadedRubricResults((prev) => ({
          ...prev,
          [key]: true,
        }));

        return result.recordings;
      }),
    [withLoading],
  );

  const listFavorites = useCallback(
    async (): Promise<Recording[]> =>
      withLoading(async () => {
        await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate network delay
        const data = await listFavoritesApi();
        setFavorites(data);
        setFavoriteIDs(data.map((item) => item.recordingID));
        setHasLoadedFavorites(true);
        setHasLoadedFavoriteIDs(true);
        return data;
      }),
    [withLoading],
  );

  const listFavoriteIDs = useCallback(
    async (): Promise<string[]> =>
      withLoading(async () => {
        const ids = await listFavoriteIDsApi();
        setFavoriteIDs(ids);
        setHasLoadedFavoriteIDs(true);
        return ids;
      }),
    [withLoading],
  );

  const addFavorite = useCallback(
    async (recordingID: string, recording?: Recording): Promise<void> => {
      setError(null);
      try {
        await addFavoriteApi(recordingID);

        setFavoriteIDs((prev) =>
          prev.includes(recordingID) ? prev : [...prev, recordingID],
        );

        setFavorites((prev) => {
          if (prev.some((item) => item.recordingID === recordingID)) {
            return prev;
          }

          const allLoaded = uniqueRecordings([
            ...Object.values(recordingsByCategory).flat(),
            ...Object.values(recordingsByRubric).flat(),
          ]);
          const favoriteCandidate =
            recording ??
            allLoaded.find((item) => item.recordingID === recordingID);
          return favoriteCandidate ? [...prev, favoriteCandidate] : prev;
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unexpected error";
        setError(message);
        throw err;
      }
    },
    [recordingsByCategory, recordingsByRubric],
  );

  const removeFavorite = useCallback(
    async (recordingID: string): Promise<void> => {
      setError(null);
      try {
        await removeFavoriteApi(recordingID);
        setFavoriteIDs((prev) => prev.filter((id) => id !== recordingID));
        setFavorites((prev) =>
          prev.filter((item) => item.recordingID !== recordingID),
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unexpected error";
        setError(message);
        throw err;
      }
    },
    [],
  );

  const isFavorite = useCallback(
    (recordingID: string): boolean => favoriteIDs.includes(recordingID),
    [favoriteIDs],
  );

  const searchRecordings = useCallback(
    (query: string, filters?: SearchFilters): Recording[] => {
      const normalizedQuery = query.trim().toLowerCase();

      let source: Recording[];
      if (filters?.category && filters.rubricID) {
        source =
          recordingsByRubric[
            buildRubricFilterKey(filters.category, filters.rubricID)
          ] ?? [];
      } else if (filters?.category) {
        source = recordingsByCategory[filters.category] ?? [];
      } else {
        source = uniqueRecordings([
          ...Object.values(recordingsByCategory).flat(),
          ...Object.values(recordingsByRubric).flat(),
          ...favorites,
        ]);
      }

      if (!normalizedQuery) {
        return source;
      }

      return source.filter((recording) => {
        const searchableText = [
          recording.title,
          recording.preacherName,
          recording.rubricTitle,
          recording.category,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(normalizedQuery);
      });
    },
    [favorites, recordingsByCategory, recordingsByRubric],
  );

  const getRecordingFiles = useCallback(
    async (
      recordingID: string,
      forceRefresh = false,
    ): Promise<RecordingFilesCacheEntry> => {
      const cached = recordingFilesMap.get(recordingID);

      // await new Promise((resolve) => setTimeout(resolve, 10000)); // Simulate network delay

      if (!forceRefresh && cached && Date.now() < toEpochMs(cached.expiresAt)) {
        return cached;
      }

      const files = await getRecordingFilesApi(recordingID);
      const cacheEntry: RecordingFilesCacheEntry = {
        ...files,
        fetchedAt: Date.now(),
      };

      setRecordingFilesMap((prev) => {
        const next = new Map(prev);
        next.set(recordingID, cacheEntry);
        return next;
      });

      return cacheEntry;
    },
    [recordingFilesMap],
  );

  const value: RecordingContextModel = {
    rubrics,
    recordingsByCategory,
    recordingsByRubric,
    favorites,
    favoriteIDs,
    hasLoadedRubrics,
    hasLoadedFavorites,
    hasLoadedFavoriteIDs,
    hasInitialized,
    setHasInitialized,
    selectedRubricIDs,
    setSelectedRubricIDs,
    archiveState,
    setArchiveState,
    hasLoadedCategory: (category) => Boolean(loadedCategories[category]),
    hasLoadedRubricResult: (category, rubricID) =>
      Boolean(loadedRubricResults[buildRubricFilterKey(category, rubricID)]),
    recordingFilesMap,
    isLoading,
    error,
    listRubrics,
    listRecordingsByCategory,
    listRecordingsByRubric,
    listFavorites,
    listFavoriteIDs,
    addFavorite,
    removeFavorite,
    isFavorite,
    searchRecordings,
    getRecordingFiles,
  };

  return (
    <RecordingContext.Provider value={value}>
      {children}
    </RecordingContext.Provider>
  );
};

export const useRecordings = (): RecordingContextModel => {
  const ctx = useContext(RecordingContext);
  if (!ctx) {
    throw new Error("useRecordings must be used within <RecordingProvider>");
  }
  return ctx;
};
